import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import styles from "./Blog_list.module.css";
import globalStyles from "../../components/servicecard/Servicecard.module.css";
import { generateBlogSitemap } from "../../utils/generateBlogSitemap";
import Masonry from "react-masonry-css";
import SeoPage from "../../components/seo/SeoPage";

const API_BASE_URL = AxiosInstance.defaults.baseURL;

const BlogCard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [openCategory, setOpenCategory] = useState(false);
  const [openTag, setOpenTag] = useState(false);
  const [filterTop, setFilterTop] = useState("85vh");
  const [loading, setLoading] = useState(true);

  // Breakpoint columns configuration
  const breakpointColumns = {
    default: 3,
    992: 2,
    576: 1,
  };

  // Initialize filters on mount
  useEffect(() => {
    const stateCategory = location.state?.selectedCategory || "";
    const stateTag = location.state?.selectedTag || "";

    setSelectedCategory(stateCategory);
    setSelectedTag(stateTag);
    fetchBlogs(stateCategory, stateTag);
  }, []);

  useEffect(() => {
    generateBlogSitemap();
  }, []);

  const fetchBlogs = async (categoryId = "", tagId = "") => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/api/blog/list", {
        blog_status: 1,
        tag_id: tagId,
        author_name: "",
        author_designation: "",
        categories_id: categoryId,
        start_date: "",
        end_date: "",
        search: "",
      });
      setBlogs(response.data.status ? response.data.blog || [] : []);
    } catch (err) {
      console.error(err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await AxiosInstance.get("/api/blog/categorieslist");
      if (res.data.status) setCategories(res.data.tag);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await AxiosInstance.get("/api/blog/taglist");
      if (res.data.status) setTags(res.data.tag || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;

      if (scrollY + windowHeight >= docHeight - 100) {
        setFilterTop("10vh");
      } else {
        setFilterTop("85vh");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <SeoPage
        title="Bharat Clouds Blog | Know more insights, trends, stories from us"
        description="Stay updated with the Bharat Clouds Blog and learn more about the latest tech insights, industry trends ideas shaping future"
      />
      <div className={`container-fluid my-5 mt-5 ${styles.fullWidthContainer}`}>
        {/* Filter Bar */}
        <div className={styles.filtersBar} style={{ top: filterTop }}>
          <div className={styles.title}>Filters</div>

          {/* Category Dropdown */}
          <div className={styles.dropdownWrapper}>
            <div className={styles.dropdownSelected}>
              <span
                onClick={() => {
                  if (selectedCategory) {
                    setSelectedCategory("");
                    fetchBlogs("", selectedTag);
                  } else {
                    setOpenCategory(!openCategory);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {selectedCategory
                  ? categories.find((c) => c.id === selectedCategory)?.name
                  : "Category"}{" "}
                <span className={styles.arrow}>
                  {selectedCategory ? "✕" : "▼"}
                </span>
              </span>
            </div>

            {openCategory && (
              <div className={styles.dropdownOptions}>
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={styles.option}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setOpenCategory(false);
                      fetchBlogs(cat.id, selectedTag);
                    }}
                  >
                    {cat.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tag Dropdown */}
          <div className={styles.dropdownWrapper}>
            <div className={styles.dropdownSelected}>
              <span
                onClick={() => {
                  if (selectedTag) {
                    setSelectedTag("");
                    fetchBlogs(selectedCategory, "");
                  } else {
                    setOpenTag(!openTag);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {selectedTag
                  ? tags.find((t) => t.id === selectedTag)?.name
                  : "Tags"}{" "}
                <span className={styles.arrow}>{selectedTag ? "✕" : "▼"}</span>
              </span>
            </div>

            {openTag && (
              <div className={styles.dropdownOptions}>
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    className={styles.option}
                    onClick={() => {
                      setSelectedTag(tag.id);
                      setOpenTag(false);
                      fetchBlogs(selectedCategory, tag.id);
                    }}
                  >
                    {tag.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Heading */}
        <div className="text-center">
          <h5 className={`mt-3 main_heading ${globalStyles.service_heading}`}>
            Ideas. Insights. Innovation.
          </h5>
          <p
            className={`main_subheading ${globalStyles.service_subheading} p-0`}
          >
            Perspectives and stories from the ever-evolving world of technology.
          </p>
        </div>

        {/* Blog Grid - Masonry */}
        {loading ? (
          <div className={styles.noBlogs}>
            <div className="text-center">
            </div>
          </div>
        ) : blogs.length > 0 ? (
          <Masonry
            breakpointCols={breakpointColumns}
            className={styles.masonryGrid}
            columnClassName={styles.masonryGridColumn}
          >
            {blogs.map((blog, index) => {
              // ✅ Image URL is now sent as a full URL from the backend - use it as-is.
              const imageUrl = blog.cover_image_path || "";
              return (
                <div key={blog.id} className={styles.blogCard}>
                  <div
                    onClick={() => {
                      // ✅ Navigate directly to the prebuilt static blog HTML
                      // using the backend-provided `live_file_path`.
                      if (blog.live_file_path) {
                        window.location.href = blog.live_file_path;
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={imageUrl}
                      className={styles.cardImage}
                      alt={blog.cover_image_originalname || "Blog Cover"}
                    />
                    <div className={styles.cardBody}>
                      <div className={styles.titleClamp}>{blog.title}</div>
                      <div className={styles.description}>
                        {(() => {
                          const div = document.createElement("div");
                          div.innerHTML = blog.content || "";
                          const cleanText =
                            div.textContent || div.innerText || "";
                          return cleanText
                            .split(/\s+/)
                            .slice(0, 120)
                            .join(" ")
                            .split("\n")
                            .map((line, idx) =>
                              line.trim() ? <p key={idx}>{line}</p> : null,
                            );
                        })()}
                      </div>
                    </div>
                    <div className={styles.footerText}>
                      <p>
                        {new Date(blog.posted_date).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          },
                        )}
                      </p>
                      <p
                        title={`${blog.author_name}, ${blog.author_designation}`}
                      >
                        {blog.author_name}, {blog.author_designation}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </Masonry>
        ) : (
          <div className={styles.noBlogs}>
            <p className="text-center">
              No blogs are present for the filter you applied.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogCard;